/**
 * Grouped dashboard table — group rows (Kunde or ProduktType) and the instance
 * rows inside an expanded group.
 *
 * Notifications are created by a confirmed product assessment and sent from the
 * Benachrichtigungen page (DEV-100); the status cells only show how far an
 * instance is and link there.
 */

import { type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Chip } from '../../components/Chip';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { Icon } from '../../components/Icon';
import { DASH, countLabel, fmtDate, fmtVersion } from '../../lib/format';
import type { OverviewRow } from '../../lib/types';
import {
  auditLogLink,
  type GateMap,
  type GroupMode,
  type OverviewGroup,
  type WorkState,
} from './dashboardGroups';
import { orFallback, type NotifyGate } from './dashboardModel';
import { ComponentsCell, CvesCell, KomponentenDetail } from './ProductCells';
import type { ExploitabilityStore } from './useExploitability';

/** Rows per page; below that the pagination bars are just noise. */
const PAGE_SIZE = 10;

function pageSizeFor(count: number): number | undefined {
  return count > PAGE_SIZE ? PAGE_SIZE : undefined;
}

/** Rows toggle on click; interactive cells swallow it. */
const swallow = (event: MouseEvent): void => event.stopPropagation();

/* ------------------------------------------------------------------------- *
 * Shared props
 * ------------------------------------------------------------------------- */

export interface StatusProps {
  gates: GateMap;
  /** `triage.read` — only then is a link to the triage queue useful. */
  canReadTriage: boolean;
}

/** The triage queue filtered to one instance (its serial number is searchable there). */
function triageLink(row: OverviewRow): string {
  const serial = row.produkt.seriennummer?.trim();
  return serial ? `/triage?q=${encodeURIComponent(serial)}` : '/triage';
}

/* ------------------------------------------------------------------------- *
 * Status — per instance and summarised per group
 * ------------------------------------------------------------------------- */

function InstanceStatus({
  row,
  gate,
  canReadTriage,
}: {
  row: OverviewRow;
  gate: NotifyGate | undefined;
  canReadTriage: boolean;
}): ReactNode {
  if (gate === undefined || gate.kind === 'clean') {
    return (
      <span className="muted" title="Ohne KEV-Treffer ist nichts zu melden">
        {DASH}
      </span>
    );
  }
  if (gate.kind === 'notified') {
    return (
      <div className="dash-status">
        <Chip tone="good">benachrichtigt</Chip>
        <span className="prod">{fmtDate(gate.versendetAm)}</span>
        {/* The Audit-Log is guarded by triage.read, like the triage links. */}
        {canReadTriage ? (
          <Link to={auditLogLink(row)} onClick={swallow}>
            Im Audit-Log ansehen
          </Link>
        ) : null}
      </div>
    );
  }
  if (gate.kind === 'ready') {
    if (!gate.kundeResolvable) return <span className="muted">kein Kunde zugeordnet</span>;
    return (
      <div className="dash-status" onClick={swallow}>
        <Chip tone="warn" title="Produktbewertung „Betroffen“ bestätigt, Benachrichtigung noch nicht versendet">
          {countLabel(gate.openCount, 'Benachrichtigung', 'Benachrichtigungen')} offen
        </Chip>
        {canReadTriage ? <Link to="/benachrichtigungen">Zur Benachrichtigung</Link> : null}
      </div>
    );
  }
  if (gate.openEvents > 0) {
    return (
      <div className="dash-status" onClick={swallow}>
        <span className="muted">Bewertung ausstehend</span>
        {canReadTriage ? (
          <Link to={triageLink(row)}>Zur Bewertung</Link>
        ) : null}
      </div>
    );
  }
  return (
    <span
      className="muted"
      title={
        gate.totalEvents > 0
          ? 'Alle Bewertungen dieser Instanz sind „Nicht betroffen“ oder „Behoben“.'
          : 'Für diese Instanz liegt noch keine Bewertung vor. Der KEV-Scan legt sie an.'
      }
    >
      {gate.totalEvents > 0 ? 'nicht anwendbar' : 'keine Bewertung'}
    </span>
  );
}

/** Share of affected rows: red for affected, green for the rest. */
function AffectedBar({ affected, total }: { affected: number; total: number }): ReactNode {
  if (total <= 0) return null;
  const share = Math.min(100, Math.max(0, (affected / total) * 100));
  return (
    <span className="dash-affected-bar" aria-hidden="true">
      <span style={{ width: `${share}%` }} />
    </span>
  );
}

/** The triage queue searched for the group's customer or product. */
function groupTriageLink(group: OverviewGroup): string {
  return `/triage?q=${encodeURIComponent(group.title)}`;
}

/** Status of a group within its work section. */
function GroupStatus({
  group,
  section,
  canReadTriage,
}: {
  group: OverviewGroup;
  section: WorkState;
  canReadTriage: boolean;
}): ReactNode {
  const count = group.rows.length;
  if (section === 'assess') {
    return (
      <div className="dash-status" onClick={swallow}>
        <Chip tone="warn" title="KEV-Treffer ohne bestätigte Produktbewertung">
          {count} zu bewerten
        </Chip>
        {canReadTriage ? <Link to={groupTriageLink(group)}>Zur Bewertung</Link> : null}
      </div>
    );
  }
  if (section === 'notify') {
    return (
      <div className="dash-status" onClick={swallow}>
        <Chip tone="warn" title="Produktbewertung „Betroffen“ bestätigt, Benachrichtigung noch nicht versendet">
          {count} zu versenden
        </Chip>
        {canReadTriage ? <Link to="/benachrichtigungen">Zur Benachrichtigung</Link> : null}
      </div>
    );
  }
  if (section === 'notified') {
    return (
      <Chip tone="good" title="Bezogen auf alle Instanzen, die eine Benachrichtigung brauchen">
        {group.notified >= group.due ? 'Alle benachrichtigt' : `${group.notified} von ${group.due} benachrichtigt`}
      </Chip>
    );
  }
  return group.affected === 0 ? (
    <Chip tone="good">keine KEV-Treffer</Chip>
  ) : (
    <span className="muted" title="Alle Bewertungen sind „Nicht betroffen“ oder „Behoben“.">
      nicht anwendbar
    </span>
  );
}

/* ------------------------------------------------------------------------- *
 * Instance table (inside an expanded group)
 * ------------------------------------------------------------------------- */

function instanceLabel(row: OverviewRow): string {
  const version = fmtVersion(row.produktversion.version_nr);
  return [
    row.produkt.seriennummer ? `S/N ${row.produkt.seriennummer}` : null,
    row.produktversion.version_nr ? (version === 'Demo' ? version : `V${version}`) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

interface InstanceTableProps extends StatusProps {
  group: OverviewGroup;
  store: ExploitabilityStore;
}

function InstanceTable({
  group,
  store,
  gates,
  canReadTriage,
}: InstanceTableProps): ReactNode {
  const columns: DataTableColumn<OverviewRow>[] = [
    {
      key: 'instanz',
      header: group.mode === 'kunde' ? 'Produkt' : 'Kunde',
      width: '32%',
      sortValue: (row) =>
        group.mode === 'kunde' ? row.produkttyp.bezeichnung : row.kunde.name,
      render: (row) => (
        <>
          <div className="kunde-name">
            {group.mode === 'kunde'
              ? orFallback(row.produkttyp.bezeichnung, 'Unbekannter Produkttyp')
              : orFallback(row.kunde.name, 'Unbekannter Kunde')}
            {group.mode === 'produkt' && row.kunde.ist_eu ? (
              <>
                {' '}
                <Chip tone="info" title="Kunde in der EU: CRA-Meldepflichten gelten">
                  EU
                </Chip>
              </>
            ) : null}
          </div>
          <div className="prod">{instanceLabel(row) || DASH}</div>
        </>
      ),
    },
    {
      key: 'komponenten',
      header: 'Betroffene Komponenten',
      width: '30%',
      sortValue: (row) => row.affected_count,
      render: (row) => <ComponentsCell row={row} />,
    },
    {
      key: 'cves',
      header: 'KEV-CVEs',
      width: '20%',
      sortValue: (row) => row.kev_cves.length,
      render: (row) => <CvesCell cves={row.kev_cves} max={3} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: '18%',
      render: (row) => (
        <InstanceStatus row={row} gate={gates.get(row.produkt.id)} canReadTriage={canReadTriage} />
      ),
    },
  ];

  return (
    <div className="dash-subwrap">
      <DataTable
        rows={group.rows}
        columns={columns}
        rowKey={(row) => row.produkt.id}
        renderDetail={(row) => <KomponentenDetail row={row} store={store} />}
        mobileBreakpoint={720}
        pageSize={pageSizeFor(group.rows.length)}
        paginationResetKey={group.key}
        resetPageOnRowsChange={false}
        caption={group.mode === 'kunde' ? 'Produkte des Kunden' : 'Auslieferungen des Produkts'}
      />
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Group table
 * ------------------------------------------------------------------------- */

export interface GroupTableProps extends StatusProps {
  mode: GroupMode;
  section: WorkState;
  /** Accessible name of the table, e.g. the section title. */
  caption: string;
  groups: readonly OverviewGroup[];
  store: ExploitabilityStore;
  emptyText: ReactNode;
  paginationResetKey: string;
}

export function GroupTable({
  mode,
  section,
  caption,
  groups,
  store,
  emptyText,
  paginationResetKey,
  ...status
}: GroupTableProps): ReactNode {
  const columns: DataTableColumn<OverviewGroup>[] = [
    {
      key: 'name',
      header: mode === 'kunde' ? 'Kunde' : 'Produkt',
      width: '34%',
      sortValue: (group) => group.title,
      render: (group) => (
        <>
          <div className="kunde-name">
            {group.title}
            {group.euKunde ? (
              <>
                {' '}
                <Chip tone="info" title="Kunde in der EU: CRA-Meldepflichten gelten">
                  EU
                </Chip>
              </>
            ) : null}
          </div>
          <div className="prod">
            {mode === 'kunde'
              ? group.subtitle !== null
                ? <><Icon name="mail" size={12} /> {group.subtitle}</>
                : group.hasKunde
                  ? <><Icon name="warning" size={12} /> keine Kontakt-E-Mail</>
                  : 'Produktinstanz ohne Kunde'
              : [
                  group.subtitle,
                  `${countLabel(group.total, 'Auslieferung', 'Auslieferungen')} bei ${countLabel(
                    group.counterparts,
                    'Kunde',
                    'Kunden',
                  )}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
          </div>
        </>
      ),
    },
    {
      key: 'betroffen',
      header: 'Betroffen',
      width: '18%',
      sortValue: (group) => group.affected,
      render: (group) => (
        <>
          <span className="count-pill">{group.affected}</span>{' '}
          <span className="muted">
            von {countLabel(group.total, mode === 'kunde' ? 'Produkt' : 'Auslieferung', mode === 'kunde' ? 'Produkten' : 'Auslieferungen')}
          </span>
          <AffectedBar affected={group.affected} total={group.total} />
        </>
      ),
    },
    {
      key: 'cves',
      header: 'KEV-CVEs',
      width: '22%',
      sortValue: (group) => group.cves.length,
      render: (group) => <CvesCell cves={group.cves} max={3} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: '26%',
      headerTitle: 'Benachrichtigungen entstehen mit der bestätigten Produktbewertung „Betroffen“ in der Triage.',
      sortValue: (group) => (section === 'notified' ? group.notified / Math.max(1, group.due) : group.rows.length),
      render: (group) => <GroupStatus group={group} section={section} canReadTriage={status.canReadTriage} />,
    },
  ];

  return (
    <DataTable
      rows={groups}
      columns={columns}
      rowKey={(group) => group.key}
      renderDetail={(group) => <InstanceTable group={group} store={store} {...status} />}
      emptyText={emptyText}
      mobileBreakpoint={760}
      pageSize={pageSizeFor(groups.length)}
      paginationResetKey={paginationResetKey}
      resetPageOnRowsChange={false}
      caption={caption}
    />
  );
}
